import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { NavLink } from 'react-router-dom';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { Breadcrumbs, Button, Stack, Typography } from '@mui/material';
import BackIcon from '@mui/icons-material/ArrowBack';
import { AlertProps } from '@mui/material/Alert';
import { useSnackbar } from '../../../contexts/SnackBarContext';
import { PageSection } from '../../subs/PageSection';
import { BreadCrumbProps } from "../../subs/PageAlternate";
import { httpGet } from "../../Web";
import { ApiResponse } from "../../types/ApiResponse";

// global theming ruins the back button, disable it here
const backTheme = createTheme({
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                },
            },
        }
    }
});

const breadcrumbs: BreadCrumbProps[] = [
    { to: "/Administration", text: "Administration" },
];

export const getLogList = (abortController: AbortController): Promise<ApiResponse<string[]>> =>
    httpGet('/api/dev/getloglist', abortController);

export const LogFileAdminPage = () => {
    const navigate = useNavigate();
    const { showSnackbar } = useSnackbar();
    const [logFiles, setLogFiles] = React.useState<string[]>([]);

    const showUserFeedback = React.useCallback((msg: string | null, severity: AlertProps['severity'] = 'error') => {
        msg && showSnackbar(msg, severity);
    }, [showSnackbar]);

    React.useEffect(() => {
        const fetchLogFileData = async () => {
            const logListResponse = await getLogList(new AbortController());
            if (logListResponse.failed) {
                showUserFeedback(logListResponse.errorText);
                return;
            }
            setLogFiles(logListResponse.result);
        }
        fetchLogFileData();
    }, [showUserFeedback]);

    const backButtonHandler = () => navigate(-1);

    return <>
        <Stack direction="row" spacing={200}>
            <ThemeProvider theme={backTheme}>
                <Button startIcon={<BackIcon />} size="small" onClick={backButtonHandler}>back</Button>
            </ThemeProvider>
            {breadcrumbs && <Breadcrumbs separator="|">
                {breadcrumbs.map((crumb, index) => (
                    <NavLink key={index} to={crumb.to}>{crumb.text}</NavLink>
                ))}
            </Breadcrumbs>}
        </Stack>
        <PageSection>
            {
                [
                    <Stack spacing={2}>
                        <Typography variant={'h5'}>
                            {"Log File Administration"}
                        </Typography>
                        {logFiles.length > 0 ? (
                            logFiles.map((file, index) => (
                                <Button
                                    key={index}
                                    variant="outlined"
                                    component="a"
                                    href={`/api/dev/downloadlog?fileName=${encodeURIComponent(file)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {file}
                                </Button>
                            ))
                        ) : (
                            <Typography variant="body1">No log files available.</Typography>
                        )}
                    </Stack>
                ]
            }
        </PageSection>
    </>
}